import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { STATUS_LABEL, TYPE_LABEL } from "@/lib/constants";
import { toast } from "sonner";
import {
  Sparkles, Bot, UserCheck, ZoomIn, ZoomOut, RotateCw, Loader2, Check,
  RefreshCw, AlertTriangle, FileCheck2, Inbox, CheckCircle2, XCircle,
  Download, History, CalendarRange,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

async function downloadPhoto(path, filename) {
  try {
    const res = await fetch(fileUrl(path));
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = (filename || "foto").replace(/[^\w.-]+/g, "_") + ".jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch {
    toast.error("Gagal mengunduh foto");
  }
}

function ReviewHistory() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/review/history", { params }).then((r) => setList(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  return (
    <div className="space-y-4" data-testid="review-history">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-slate-500 flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> Dari Tanggal</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-xl w-44" data-testid="history-date-from" />
        </div>
        <div>
          <Label className="text-xs text-slate-500 flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> Sampai Tanggal</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-xl w-44" data-testid="history-date-to" />
        </div>
        {(from || to) && (
          <Button variant="ghost" size="sm" className="rounded-xl text-slate-500" onClick={() => { setFrom(""); setTo(""); }} data-testid="history-clear-filter">Hapus Filter</Button>
        )}
        <span className="ml-auto text-sm text-slate-500" data-testid="history-count">{list.length} tugas dinilai</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      ) : list.length === 0 ? (
        <p className="text-sm text-slate-400 bg-white rounded-2xl p-8 border border-slate-100 text-center">
          <History className="w-8 h-8 mx-auto mb-2 opacity-40" />Belum ada tugas yang dinilai pada rentang ini.
        </p>
      ) : (
        <div className="space-y-3">
          {list.map((g) => {
            const photos = (g.photo_paths && g.photo_paths.length) ? g.photo_paths : (g.photo_path ? [g.photo_path] : []);
            return (
              <div key={g.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`history-item-${g.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800">{g.student_name}</p>
                    <p className="text-sm text-slate-500">{g.assignment_title}{g.subject_name ? ` · ${g.subject_name}` : ""}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Dinilai {(g.graded_at || "").slice(0, 16).replace("T", " ")}{g.graded_by ? ` · ${g.graded_by}` : ""}</p>
                  </div>
                  <span className={`font-display text-2xl font-bold ${g.final_score >= 75 ? "text-emerald-600" : "text-orange-500"}`} data-testid={`history-score-${g.id}`}>{g.final_score}</span>
                </div>
                {g.teacher_feedback && <p className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg px-3 py-2">{g.teacher_feedback}</p>}
                {photos.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {photos.map((p, i) => (
                      <div key={i} className="relative">
                        <img src={fileUrl(p)} alt={`jawaban ${i + 1}`} className="w-20 h-20 rounded-lg object-cover border border-slate-200 bg-slate-100" />
                        <button onClick={() => downloadPhoto(p, `${g.student_name}-${g.assignment_title}-${i + 1}`)}
                          className="absolute bottom-1 right-1 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-lg transition-colors"
                          title="Unduh foto" data-testid={`history-download-${g.id}-${i}`}>
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ReviewCenter() {
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [finalScore, setFinalScore] = useState("");
  const [feedback, setFeedback] = useState("");
  const [transcript, setTranscript] = useState("");
  const [batch, setBatch] = useState(null);

  const loadQueue = () => {
    setLoading(true);
    api.get("/review/queue").then((r) => setQueue(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { loadQueue(); }, []);

  const openSub = async (sub) => {
    setSelected(sub.id);
    setZoom(1); setRot(0); setPhotoIdx(0);
    const { data } = await api.get(`/submissions/${sub.id}`);
    setDetail(data);
    const ai = data.ai_analysis || {};
    setFinalScore(data.final_score ?? ai.suggested_score ?? "");
    setFeedback(data.teacher_feedback || ai.study_notes || "");
    setTranscript(ai.transcription || "");
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/submissions/${selected}/analyze`);
      toast.success("Analisis AI selesai");
      const ai = data.ai_analysis;
      setDetail((d) => ({ ...d, ai_analysis: ai, status: "menunggu_review" }));
      setTranscript(ai.transcription || "");
      if (ai.suggested_score != null) setFinalScore(ai.suggested_score);
      if (ai.study_notes) setFeedback(ai.study_notes);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analisis AI gagal");
    } finally { setAnalyzing(false); }
  };

  const saveGrade = async (status) => {
    if (finalScore === "" || finalScore === null) { toast.error("Isi nilai final dulu"); return; }
    setSaving(true);
    try {
      await api.put(`/submissions/${selected}/grade`, {
        final_score: parseFloat(finalScore), teacher_feedback: feedback,
        status, transcription: transcript || null,
      });
      toast.success(status === "dinilai" ? "Nilai final disimpan ✅" : "Permintaan revisi dikirim");
      loadQueue();
      setSelected(null); setDetail(null);
    } catch (e) {
      toast.error("Gagal menyimpan nilai");
    } finally { setSaving(false); }
  };

  const rejectSub = async () => {
    setRejecting(true);
    try {
      await api.post(`/submissions/${selected}/reject`);
      toast.success("Tugas dikembalikan ke murid untuk dikerjakan ulang");
      setConfirmReject(false);
      loadQueue();
      setSelected(null); setDetail(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menolak tugas");
    } finally { setRejecting(false); }
  };

  const hasPhotos = (q) => (q.photo_paths && q.photo_paths.length) || q.photo_path;
  const isPhotoTask = (q) => q.type !== "online" && hasPhotos(q);
  const isPending = (q) => isPhotoTask(q) && !q.has_analysis;

  const analyzeAll = async () => {
    const eligible = queue.filter(isPending);
    if (eligible.length === 0) { toast.error("Tidak ada tugas foto baru untuk dianalisis"); return; }
    setBatch({ done: 0, total: eligible.length, fail: 0 });
    let done = 0, fail = 0;
    for (const q of eligible) {
      try {
        await api.post(`/submissions/${q.id}/analyze`);
      } catch (e) { fail++; }
      done++;
      setBatch({ done, total: eligible.length, fail });
    }
    toast.success(`Analisis selesai: ${done - fail} berhasil${fail ? `, ${fail} gagal` : ""}`, { duration: 7000 });
    setBatch(null);
    loadQueue();
    if (selected) openSub({ id: selected });
  };

  const ai = detail?.ai_analysis || {};
  const isOnline = detail?.type === "online";
  const subPhotos = detail ? ((detail.photo_paths && detail.photo_paths.length) ? detail.photo_paths : (detail.photo_path ? [detail.photo_path] : [])) : [];
  const pendingCount = queue.filter(isPending).length;
  const photoTaskCount = queue.filter(isPhotoTask).length;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-indigo-500" /> Pusat Review AI
        </h1>
        <p className="text-slate-500 mt-1">Periksa saran nilai dari AI, perbaiki bila perlu, lalu setujui nilai akhir. Nilai final ada di tanganmu.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <Tabs defaultValue="queue" className="lg:col-span-12">
          <TabsList className="rounded-xl">
            <TabsTrigger value="queue" className="rounded-lg" data-testid="tab-review-queue"><FileCheck2 className="w-4 h-4 mr-1.5" /> Antrean Review</TabsTrigger>
            <TabsTrigger value="history" className="rounded-lg" data-testid="tab-review-history"><History className="w-4 h-4 mr-1.5" /> Riwayat Dinilai</TabsTrigger>
          </TabsList>
          <TabsContent value="queue" className="mt-6">
            <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <span className="font-display font-semibold text-slate-800">Antrean ({queue.length})</span>
              {pendingCount > 0 && (
                <Button onClick={analyzeAll} disabled={!!batch} className="w-full mt-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-sm" data-testid="analyze-all-btn">
                  {batch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {batch ? `Menganalisis ${batch.done}/${batch.total}...` : `Analisis Semua (${pendingCount})`}
                </Button>
              )}
              {batch && (
                <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-testid="analyze-all-progress">
                  <div className="h-full bg-indigo-500 transition-all" style={{ width: `${Math.round((batch.done / batch.total) * 100)}%` }} />
                </div>
              )}
              {!batch && photoTaskCount > 0 && pendingCount === 0 && (
                <p className="mt-2 text-[11px] text-emerald-600" data-testid="all-analyzed-hint">Semua tugas foto sudah dianalisis AI ✓</p>
              )}
              {!batch && queue.length > 0 && photoTaskCount === 0 && (
                <p className="mt-2 text-[11px] text-slate-400" data-testid="no-ai-tasks-hint">Belum ada tugas foto di antrean untuk dianalisis sekaligus.</p>
              )}
            </div>
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
            ) : queue.length === 0 ? (
              <p className="p-6 text-sm text-slate-400 text-center"><Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />Tidak ada tugas untuk direview.</p>
            ) : (
              <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-50">
                {queue.map((q) => {
                  const st = STATUS_LABEL[q.status] || STATUS_LABEL.dikumpulkan;
                  return (
                    <button key={q.id} onClick={() => openSub(q)} data-testid={`review-item-${q.id}`}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${selected === q.id ? "bg-teal-50" : ""}`}>
                      <p className="text-sm font-medium text-slate-800 truncate">{q.student_name}</p>
                      <p className="text-xs text-slate-500 truncate">{q.assignment_title}</p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                        {q.ai_enabled && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">AI</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* dual panel */}
        {!detail ? (
          <div className="lg:col-span-9 bg-white rounded-2xl border border-slate-100 shadow-sm flex items-center justify-center py-28 text-slate-400">
            <div className="text-center"><FileCheck2 className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>Pilih tugas dari antrean untuk mulai mereview.</p></div>
          </div>
        ) : (
          <div className="lg:col-span-9 grid lg:grid-cols-2 gap-6">
            {/* left: photo / answers */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <h3 className="font-display font-semibold text-slate-800 mb-3">{isOnline ? "Jawaban Murid" : "Foto Jawaban Tulisan Tangan"}</h3>
              {isOnline ? (
                <div className="space-y-2">
                  {(detail.answers || []).map((a, i) => (
                    <div key={i} className="flex justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                      <span className="text-slate-500">Soal {a.no}</span>
                      <span className="font-medium text-slate-800">{a.answer}</span>
                    </div>
                  ))}
                  {(!detail.answers || detail.answers.length === 0) && <p className="text-sm text-slate-400">Tidak ada jawaban.</p>}
                </div>
              ) : (subPhotos.length > 0) ? (
                <>
                  <div className="flex gap-2 mb-3">
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setZoom((z) => Math.min(z + 0.25, 3))} data-testid="ai-review-photo-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))} data-testid="ai-review-photo-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setRot((r) => (r + 90) % 360)} data-testid="ai-review-photo-rotate"><RotateCw className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => downloadPhoto(subPhotos[photoIdx], `${detail?.student_name || "jawaban"}-${photoIdx + 1}`)} data-testid="ai-review-photo-download"><Download className="w-4 h-4" /></Button>
                    {subPhotos.length > 1 && <span className="ml-auto text-xs text-slate-500 self-center" data-testid="ai-review-photo-count">Foto {photoIdx + 1} / {subPhotos.length}</span>}
                  </div>
                  <div className="overflow-auto max-h-[60vh] rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center p-2">
                    <img src={fileUrl(subPhotos[photoIdx])} alt="jawaban" style={{ transform: `scale(${zoom}) rotate(${rot}deg)` }} className="transition-transform origin-center max-w-full" data-testid="ai-review-photo" />
                  </div>
                  {subPhotos.length > 1 && (
                    <div className="flex gap-2 mt-2 flex-wrap" data-testid="ai-review-photo-thumbs">
                      {subPhotos.map((p, i) => (
                        <button key={i} type="button" onClick={() => { setPhotoIdx(i); setZoom(1); setRot(0); }} className={`w-14 h-14 rounded-lg overflow-hidden border-2 ${i === photoIdx ? "border-indigo-500" : "border-slate-200"}`} data-testid={`ai-review-thumb-${i}`}>
                          <img src={fileUrl(p)} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-400">Tidak ada foto.</p>
              )}

              {!isOnline && (
                <Button onClick={analyze} disabled={analyzing} className="w-full mt-4 rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="run-ai-analysis-btn">
                  {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
                  {ai.transcription ? "Analisis Ulang dengan AI" : "Analisis dengan AI (Gemini)"}
                </Button>
              )}
            </div>

            {/* right: transcription + grading */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
              <h3 className="font-display font-semibold text-slate-800">Transkripsi AI & Penilaian</h3>

              {ai.suggested_score != null && (
                <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3" data-testid="ai-suggested-score-badge">
                  <span className="flex items-center gap-2 text-sm font-medium text-indigo-700"><Bot className="w-4 h-4" /> Rekomendasi Skor AI</span>
                  <span className="font-display text-2xl font-bold text-indigo-700">{ai.suggested_score}</span>
                </div>
              )}

              {!isOnline && ai.transcription !== undefined && (
                <div>
                  <Label className="text-xs text-slate-500">Transkripsi (dapat diedit)</Label>
                  <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} className="mt-1 rounded-xl text-sm" rows={3} data-testid="ai-transcription-input" placeholder="Jalankan analisis AI untuk melihat transkripsi" />
                </div>
              )}

              {(ai.per_question || []).length > 0 && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {ai.per_question.map((p, i) => (
                    <div key={i} className={`text-xs rounded-lg px-3 py-2 border ${p.benar ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                      <span className="font-semibold">No {p.no}:</span> {p.jawaban_murid} <span className="text-slate-400">(kunci: {p.kunci})</span>
                      {p.catatan && <p className="text-slate-500 mt-0.5">{p.catatan}</p>}
                    </div>
                  ))}
                </div>
              )}

              {(ai.uncertainty || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="ai-uncertainty-markers">
                  <p className="text-xs font-semibold text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Bagian yang Ragu / Sulit Dibaca</p>
                  <ul className="list-disc list-inside text-xs text-amber-700 mt-1">{ai.uncertainty.map((u, i) => <li key={i}>{u}</li>)}</ul>
                </div>
              )}

              <div className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><UserCheck className="w-4 h-4" /> Nilai Final Guru</div>
                <div>
                  <Label className="text-xs text-slate-500">Nilai (0–100)</Label>
                  <Input type="number" min="0" max="100" value={finalScore} onChange={(e) => setFinalScore(e.target.value)} className="mt-1 rounded-xl" data-testid="teacher-final-score-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Catatan / Umpan Balik untuk Murid</Label>
                  <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} className="mt-1 rounded-xl text-sm" rows={2} data-testid="teacher-feedback-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => saveGrade("dinilai")} disabled={saving} className="rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="save-final-grade-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Setujui Nilai
                  </Button>
                  <Button onClick={() => saveGrade("revisi")} disabled={saving} variant="outline" className="rounded-xl text-orange-700" data-testid="request-revision-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Minta Revisi
                  </Button>
                </div>
                <Button onClick={() => setConfirmReject(true)} disabled={saving || rejecting} variant="outline" className="w-full rounded-xl border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="reject-submission-btn">
                  <XCircle className="w-4 h-4 mr-2" /> Tolak & Kembalikan ke Murid
                </Button>
                <p className="text-[11px] text-slate-400 leading-relaxed">Gunakan tombol ini kalau pengumpulan asal-asalan atau foto tidak jelas. Tugas akan hilang dari daftar murid dan harus dikerjakan ulang.</p>
              </div>
            </div>
          </div>
        )}
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-6">
            <ReviewHistory />
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={confirmReject} onOpenChange={setConfirmReject}>
        <AlertDialogContent data-testid="reject-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tolak & Kembalikan Tugas?</AlertDialogTitle>
            <AlertDialogDescription>
              Pengumpulan murid akan dihapus dan tugas kembali muncul di akunnya untuk dikerjakan ulang. Murid akan menerima notifikasi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reject-cancel-btn">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={rejectSub} disabled={rejecting} className="bg-rose-600 hover:bg-rose-700" data-testid="reject-confirm-btn">
              {rejecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />} Ya, Kembalikan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
